import { useEffect, useMemo, useState } from "react";
import { ArrowDownCircle, Copy, Eye, EyeOff, ImagePlus, PlusCircle, Send, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import VerifiedName from "./VerifiedName";
import VerifyPhoneDialog from "./VerifyPhoneDialog";
import IdentityVerificationDialog from "./IdentityVerificationDialog";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const MANUAL_REVIEW_PLATFORM_TYPES = new Set(["chime", "venmo", "paypal", "cashapp", "ecashapp"]);
const WITHDRAWAL_PLATFORM_TYPE = "chime";
const WITHDRAWAL_PLATFORM_TYPES = new Set(["chime", "venmo", "cashapp", "ecashapp"]);
const WITHDRAWAL_METHOD_KEYS = ["cashout", "withdraw", "withdrawal", "payout"];
const FAVORITE_HOSTS = ["DRACO", "SOPHIA", "AURUM"];
const HOST_TIP_AMOUNTS = [5, 10, 15, 20];
const MIN_WITHDRAW_DOLLARS = 15;
const MIN_ONLINE_LOAD_DOLLARS = 5;
const MIN_TRANSFER_DOLLARS = 5;
const MAX_TRANSFER_DOLLARS = 50;
const PAYMENT_ICON_ASSETS = {
  card: "/assets/payments/card.png",
  applepay: "/assets/payments/applepay.png",
  googlepay: "/assets/payments/googlepay.png",
  cashapp: "/assets/payments/cashapp.png",
  chime: "/assets/payments/chime.png",
  crypto: "/assets/payments/crypto.jpg",
  paypal: "/assets/payments/paypal.jpg",
  venmo: "/assets/payments/venmo.jpg",
};
const PAYMENT_ICON_TEXT = {
  card: "CARD",
  applepay: "AP",
  googlepay: "GP",
  crypto: "CR",
  paypal: "PP",
  venmo: "V",
};

function platformKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function publicPaymentLabel(value, fallback = "Checkout") {
  return String(value || fallback)
    .replace(/\bkey\s*hub\b/gi, "")
    .replace(/\bkeyhub\b/gi, "")
    .replace(/\bxx\s*pay\b/gi, "Payout network")
    .replace(/\bxxs\s*pays?\b/gi, "Payout network")
    .replace(/\bxxspays?\b/gi, "Payout network")
    .replace(/\s+checkout\b/gi, " checkout")
    .replace(/\s+/g, " ")
    .trim() || fallback;
}

function paymentIconKey(item, label) {
  const raw = `${item?.platform_type || ""} ${item?.id || ""} ${item?.label || ""} ${label || ""}`;
  const key = platformKey(raw);
  if (key.includes("cashapp")) return "cashapp";
  if (key.includes("chime")) return "chime";
  if (key.includes("applepay")) return "applepay";
  if (key.includes("googlepay")) return "googlepay";
  if (key.includes("paypal")) return "paypal";
  if (key.includes("venmo")) return "venmo";
  if (key.includes("crypto") || key.includes("nowpayments")) return "crypto";
  if (key.includes("card") || key.includes("credit") || key.includes("debit")) return "card";
  return "card";
}

function PaymentMethodIcon({ item, label }) {
  const key = paymentIconKey(item, label);
  const image = PAYMENT_ICON_ASSETS[key];
  if (image) {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
        <img src={image} alt="" className="h-full w-full object-cover" />
      </span>
    );
  }
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#D4AF37]/35 bg-[#D4AF37]/15 text-[11px] font-black tracking-[0.08em] text-[#F8E08E]">
      {PAYMENT_ICON_TEXT[key] || "PAY"}
    </span>
  );
}

function manualPlatform(row) {
  const platformType = platformKey(row.type || "manual");
  return {
    id: row.id || row.type,
    platform_type: platformType,
    label: publicPaymentLabel(row.name || row.type || "Manual", "Manual"),
    provider: "manual",
    configured: false,
    manual: true,
    method_id: row.id,
    instructions: row.instructions || "",
    account_label: row.account_label || "",
    account_value: row.account_value || "",
    qr_code_url: row.qr_code_url || "",
  };
}

function isCashoutRow(row) {
  if ((row.provider || "manual") !== "manual") return false;
  if (!WITHDRAWAL_PLATFORM_TYPES.has(platformKey(row.type))) return false;
  const text = `${platformKey(row.name)} ${platformKey(row.instructions)}`;
  return WITHDRAWAL_METHOD_KEYS.some((key) => text.includes(key));
}

function payoutMethodHint(platformType) {
  if (platformType === "venmo") return "Venmo tag, phone, or email";
  if (platformType === "cashapp" || platformType === "ecashapp") return "Cash App $Cashtag, phone, or email";
  if (platformType === "chime") return "Chime tag, phone, or email";
  return "Payout tag, phone, or email";
}

function copyText(value, label = "Tag") {
  const text = String(value || "").trim();
  if (!text) return;
  navigator.clipboard?.writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Could not copy. Tap and hold to copy."));
}

function cashoutDisplayRow(row, rowsForType) {
  if (row?.account_value) return row;
  const fallback = rowsForType.find((candidate) => candidate.account_value && !isCashoutRow(candidate));
  if (!fallback) return row;
  return {
    ...row,
    account_label: row.account_label || fallback.account_label,
    account_value: fallback.account_value,
    qr_code_url: row.qr_code_url || fallback.qr_code_url,
  };
}

export default function WalletHeroCard({ user, className = "", onBalanceChange, onKycRequired }) {
  const [showBalance, setShowBalance] = useState(true);
  const [open, setOpen] = useState(null);
  const [verifyPhoneOpen, setVerifyPhoneOpen] = useState(false);
  const [verifyIdentityOpen, setVerifyIdentityOpen] = useState(false);
  const coins = user?.coins || 0;
  const transferLocked = Number(user?.transfer_locked_coins || 0);
  const freePlay = Number(user?.free_play || 0);
  const usd = (coins / 1000).toFixed(2);
  const freePlayUsd = (freePlay / 1000).toFixed(2);
  const transferLockedUsd = (Math.max(0, transferLocked) / 1000).toFixed(2);
  const identityExpired = Boolean(user?.identity_verification_expires_at && new Date(user.identity_verification_expires_at).getTime() <= Date.now());
  const kycStatus = identityExpired ? "rejected" : String(user?.identity_verification_status || "not_submitted");
  const openWalletAction = (action) => {
    if (action === "withdraw" && (!user?.phone_verified || !user?.phone)) {
      toast.warning("Verify your phone number before withdrawing money.");
      setVerifyPhoneOpen(true);
      return;
    }
    if (action === "withdraw" && kycStatus !== "approved") {
      toast.info("Complete your automatic Didit verification before withdrawing money.");
      if (onKycRequired) onKycRequired();
      else setVerifyIdentityOpen(true);
      return;
    }
    setOpen(action);
  };

  return (
    <>
      <div className={`relative max-w-full overflow-hidden rounded-2xl md:rounded-3xl border-2 border-[#D4AF37]/60 bg-gradient-to-br from-[#1a0f2e] via-[#0a0a1f] to-[#030705] p-5 sm:p-7 md:p-10 ${className}`} data-testid="wallet-hero">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden md:block opacity-90">
          <div className="absolute inset-0 bg-gradient-to-l from-purple-900/50 via-purple-900/20 to-transparent" />
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
            <Reel emoji="7" />
            <Reel emoji="7" />
            <Reel emoji="7" />
          </div>
          <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-[#D4AF37]/20 blur-3xl" />
          <div className="absolute right-20 -top-8 w-32 h-32 rounded-full bg-purple-500/20 blur-3xl" />
        </div>

        <div className="relative max-w-xl min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-2">
            <span>{greeting()}, </span>
            <VerifiedName user={user} name={user?.username || "player"} className="inline-flex max-w-[16rem] align-bottom" />
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]/80 font-semibold mb-2">Wallet balance</div>
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <Wallet className="w-8 h-8 sm:w-9 sm:h-9 text-[#D4AF37] shrink-0" />
              <div className="min-w-0 break-words font-display font-black text-4xl sm:text-5xl md:text-6xl tabular-nums leading-none gold-text" data-testid="wallet-balance">
                {showBalance ? `$${usd}` : "$ ..."}
              </div>
              <button onClick={() => setShowBalance((s) => !s)} data-testid="toggle-balance"
                className="p-2 rounded-full border border-white/15 hover:bg-white/10 sm:ml-2 shrink-0" title="Show/hide balance">
                {showBalance ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-black/25 px-4 py-2 text-sm text-white/70">
              <span className="text-white/45">Freeplay</span>
              <span className="font-display font-bold tabular-nums text-[#F8E08E]" data-testid="freeplay-balance">
                {showBalance ? `$${freePlayUsd}` : "$ ..."}
              </span>
            </div>
            {transferLocked > 0 && (
              <div className="mt-2 inline-flex max-w-full items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-950/25 px-4 py-2 text-xs text-emerald-100/75">
                <span className="text-emerald-100/45">Game-load only</span>
                <span className="font-display font-bold tabular-nums text-emerald-200">
                  {showBalance ? `$${transferLockedUsd}` : "$ ..."}
                </span>
              </div>
            )}
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <button
              onClick={() => openWalletAction("add")}
              data-testid="wallet-add-money"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-black font-bold hover:bg-[#F1C40F] transition-all"
            >
              <PlusCircle className="w-4 h-4" />
              Add money
            </button>
            <button
              onClick={() => openWalletAction("withdraw")}
              data-testid="wallet-withdraw-money"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/15 bg-black/20 text-white font-bold hover:border-[#D4AF37]/50 hover:bg-white/[0.06] transition-all"
            >
              <ArrowDownCircle className="w-4 h-4 text-[#D4AF37]" />
              Withdraw money
            </button>
            <button
              onClick={() => openWalletAction("transfer")}
              data-testid="wallet-transfer-money"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-emerald-500/25 bg-emerald-950/30 text-emerald-200 font-bold hover:border-emerald-400/50 hover:bg-emerald-900/30 transition-all"
            >
              <Send className="w-4 h-4 text-emerald-300" />
              Send to friend
            </button>
          </div>
        </div>
      </div>

      <VerifyPhoneDialog open={verifyPhoneOpen} onClose={() => setVerifyPhoneOpen(false)} />
      <IdentityVerificationDialog open={verifyIdentityOpen} onClose={() => setVerifyIdentityOpen(false)} onSubmitted={onBalanceChange} user={user} />
      {open === "add" && <MoneyDialog type="load" title="Add money" user={user} onClose={() => setOpen(null)} />}
      {open === "withdraw" && <MoneyDialog type="redeem" title="Withdraw money" user={user} onClose={() => setOpen(null)} />}
      {open === "transfer" && (
        <TransferDialog
          user={user}
          onClose={() => setOpen(null)}
          onBalanceChange={onBalanceChange}
        />
      )}
    </>
  );
}

function TransferDialog({ user, onClose, onBalanceChange }) {
  const [recipientUsername, setRecipientUsername] = useState("");
  const [amount, setAmount] = useState(String(MIN_TRANSFER_DOLLARS));
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const balanceDollars = (user?.coins || 0) / 1000;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const dollars = Number(amount);
    if (!recipientUsername.trim()) {
      setErr("Enter your friend's username.");
      return;
    }
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setErr("Enter a valid amount.");
      return;
    }
    if (dollars < MIN_TRANSFER_DOLLARS) {
      setErr(`Minimum transfer is $${MIN_TRANSFER_DOLLARS}.`);
      return;
    }
    if (dollars > MAX_TRANSFER_DOLLARS) {
      setErr(`Maximum transfer is $${MAX_TRANSFER_DOLLARS}.`);
      return;
    }
    if (dollars > balanceDollars) {
      setErr("Transfer amount is higher than your wallet balance.");
      return;
    }
    setLoading(true);
    try {
      const coins = Math.round(dollars * 1000);
      const { data } = await api.post("/me/wallet-transfer", {
        recipient_username: recipientUsername.trim(),
        amount: coins,
        note: note.trim(),
      });
      toast.success(`Sent $${dollars.toFixed(2)} to ${data?.recipient_username || recipientUsername.trim()}`);
      onBalanceChange?.(data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-black/70 p-3 py-5 pb-[calc(2rem+env(safe-area-inset-bottom))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-md rounded-2xl border border-emerald-500/25 bg-[#0A130E] p-4 shadow-2xl sm:p-7"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold leading-tight">Send to friend</h3>
            <p className="mt-1 text-xs leading-relaxed text-white/50">
              Transfer wallet money by username. Received transfers can load games, but cannot be cashed out directly.
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-white/10" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-950/20 p-3 text-xs leading-relaxed text-emerald-100/80">
            Min ${MIN_TRANSFER_DOLLARS}, max ${MAX_TRANSFER_DOLLARS} per transfer. Daily limit is $100. Transfers on the same device are blocked.
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-white/50">Friend username</label>
            <input
              type="text"
              required
              value={recipientUsername}
              onChange={(e) => setRecipientUsername(e.target.value)}
              placeholder="Enter username"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2.5 text-sm focus:border-emerald-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-white/50">Amount ($)</label>
            <input
              type="number"
              required
              min={MIN_TRANSFER_DOLLARS}
              max={MAX_TRANSFER_DOLLARS}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2.5 text-sm focus:border-emerald-400 focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {[5, 10, 20, 50].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setAmount(String(value))}
                className="rounded-full border border-white/10 px-3 py-1 text-xs font-bold text-white/70 hover:bg-white/5"
              >
                ${value}
              </button>
            ))}
          </div>

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-white/50">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={160}
              placeholder="Short note"
              className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2.5 text-sm focus:border-emerald-400 focus:outline-none"
            />
          </div>

          {err && <div className="rounded-lg border border-red-800/40 bg-red-900/20 p-3 text-sm text-red-300">{err}</div>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-emerald-400 py-3 font-bold text-black transition hover:bg-emerald-300 disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send wallet transfer"}
          </button>
        </form>
      </div>
    </div>
  );
}

function MoneyDialog({ type, title, user, onClose }) {
  const [amount, setAmount] = useState(type === "load" ? "20" : "10");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState({ configured: false, platforms: [], missing: [] });
  const [platform, setPlatform] = useState("");
  const [proofImage, setProofImage] = useState("");
  const [proofName, setProofName] = useState("");
  const [tipHost, setTipHost] = useState("");
  const [tipAmount, setTipAmount] = useState("");
  const [tipManual, setTipManual] = useState(false);
  const balanceDollars = (user?.coins || 0) / 1000;
  const isWithdraw = type === "redeem";
  const selectedPlatform = checkout.platforms?.find((item) => `${item.provider}:${item.id}` === platform);
  const selectedPlatformLabel = publicPaymentLabel(selectedPlatform?.label, isWithdraw ? "Cashout" : "Checkout");
  const selectedPlatformType = selectedPlatform?.platform_type || WITHDRAWAL_PLATFORM_TYPE;
  const payoutHint = payoutMethodHint(selectedPlatformType);
  const selectedAmountDollars = Number(amount || 0);
  const autoCashoutEligible = isWithdraw
    && ["chime", "cashapp", "ecashapp"].includes(selectedPlatformType)
    && Number.isFinite(selectedAmountDollars)
    && selectedAmountDollars > 0
    && selectedAmountDollars < 200;
  const onlineLoadMinDollars = !isWithdraw && selectedPlatform?.configured
    ? Math.max(MIN_ONLINE_LOAD_DOLLARS, Number(selectedPlatform?.min_amount || 0) / 1000 || 0)
    : 1;
  const onlineLoadMaxDollars = !isWithdraw && selectedPlatform?.configured && Number(selectedPlatform?.max_amount || 0) > 0
    ? Number(selectedPlatform.max_amount) / 1000
    : null;
  const allowedAmountDollars = useMemo(() => (!isWithdraw && selectedPlatform?.configured && Array.isArray(selectedPlatform?.allowed_amounts)
    ? selectedPlatform.allowed_amounts
        .map((coins) => Number(coins || 0) / 1000)
        .filter((value) => Number.isFinite(value) && value > 0)
    : []), [isWithdraw, selectedPlatform]);
  const customAmountAllowed = !isWithdraw && selectedPlatform?.configured && allowedAmountDollars.length === 0;
  const allowedAmountKey = allowedAmountDollars.join("|");
  const playHubWalletCreditDollars = useMemo(() => {
    if (isWithdraw || selectedPlatform?.provider !== "playhubslots") return null;
    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) return null;
    const coins = Math.round(dollars * 1000);
    const creditCoins = coins % 1000 === 0 ? coins : (Math.floor(coins / 1000) + 1) * 1000;
    return creditCoins / 1000;
  }, [amount, isWithdraw, selectedPlatform]);
  const needsManualProof = !isWithdraw && selectedPlatform?.manual;
  const showProofUpload = needsManualProof || isWithdraw;
  const tipDollars = Number(tipAmount || 0);
  const hasHostTip = isWithdraw && tipHost && Number.isFinite(tipDollars) && tipDollars > 0;
  const payoutAfterTip = hasHostTip ? Math.max(0, Number(amount || 0) - tipDollars) : Number(amount || 0);
  const transferLockedDollars = Math.max(0, Number(user?.transfer_locked_coins || 0)) / 1000;
  const withdrawableDollars = Math.max(0, balanceDollars - transferLockedDollars);

  useEffect(() => {
    let cancelled = false;
    const loadPlatforms = () => {
      const timestamp = Date.now();
      const requests = isWithdraw
        ? [api.get(`/payment-methods?t=${timestamp}`)]
        : [
            api.get(`/payments/playhubslots/platforms?t=${timestamp}`),
            api.get(`/payments/nowpayments/platforms?t=${timestamp}`),
            api.get(`/payment-methods?t=${timestamp}`),
          ];
      Promise.allSettled(requests).then((results) => {
        if (cancelled) return;
        if (isWithdraw) {
          const manualRows = results[0]?.status === "fulfilled" ? (results[0].value?.data || []) : [];
          const withdrawalRows = manualRows
            .filter((row) => (row.provider || "manual") === "manual")
            .map((row) => ({ ...row, platform_type: platformKey(row.type || "manual") }))
            .filter((row) => WITHDRAWAL_PLATFORM_TYPES.has(row.platform_type));
          const payoutRows = ["chime", "cashapp", "ecashapp", "venmo"].flatMap((typeKey) => {
            const rowsForType = withdrawalRows.filter((row) => row.platform_type === typeKey);
            const namedRows = rowsForType.filter(isCashoutRow);
            const selectedRows = namedRows.length ? namedRows : rowsForType;
            return selectedRows.map((row) => cashoutDisplayRow(row, rowsForType));
          });
          const payoutPlatforms = payoutRows.map(manualPlatform);
          setCheckout({ configured: false, platforms: payoutPlatforms, missing: [] });
          const first = payoutPlatforms[0];
          setPlatform((current) => {
            if (current && payoutPlatforms.some((item) => `${item.provider}:${item.id}` === current)) return current;
            return first ? `${first.provider}:${first.id}` : "";
          });
          return;
        }
        const checkoutResults = results.slice(0, -1);
        const checkoutPayloads = checkoutResults
          .filter((item) => item.status === "fulfilled")
          .map((item) => item.value?.data)
          .filter(Boolean);
        const manualResult = results[results.length - 1];
        const manualRows = manualResult?.status === "fulfilled" ? (manualResult.value?.data || []) : [];
        const platforms = checkoutPayloads.flatMap((payload) =>
          (payload.platforms || []).map((item) => ({
            ...item,
            configured: !!payload.configured,
            missing: payload.missing || [],
          }))
        );
        const configured = checkoutPayloads.some((payload) => payload.configured);
        const visiblePlatforms = configured ? platforms.filter((item) => item.configured) : platforms;
        const manualPlatforms = manualRows
          .filter((row) => (row.provider || "manual") === "manual")
          .map((row) => ({ ...row, platform_type: platformKey(row.type || "manual") }))
          .filter((row) => MANUAL_REVIEW_PLATFORM_TYPES.has(row.platform_type))
          .filter((row) => !isCashoutRow(row))
          .map(manualPlatform);
        const allPlatforms = [...visiblePlatforms, ...manualPlatforms].filter((item, index, list) =>
          index === list.findIndex((other) => other.provider === item.provider && other.id === item.id)
        );
        const missing = checkoutPayloads.flatMap((payload) => payload.missing || []);
        setCheckout({ configured, platforms: allPlatforms, missing });
        const first = allPlatforms.find((item) => item.configured) || allPlatforms[0];
        setPlatform((current) => {
          if (current && allPlatforms.some((item) => `${item.provider}:${item.id}` === current)) return current;
          return first ? `${first.provider}:${first.id}` : "";
        });
      }).catch(() => {});
    };
    loadPlatforms();
    const timer = window.setInterval(loadPlatforms, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isWithdraw]);

  useEffect(() => {
    if (!allowedAmountDollars.length) return;
    const dollars = Number(amount);
    const matches = allowedAmountDollars.some((value) => Math.abs(value - dollars) < 0.001);
    if (!matches) {
      setAmount(allowedAmountDollars[0].toFixed(2));
    }
  }, [allowedAmountDollars, allowedAmountKey, amount, platform]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setErr("Enter a valid amount.");
      return;
    }
    if (isWithdraw && dollars > withdrawableDollars) {
      setErr(`Only $${withdrawableDollars.toFixed(2)} is withdrawable. Transferred wallet credits can load games, but cannot be cashed out directly.`);
      return;
    }
    if (isWithdraw && dollars < MIN_WITHDRAW_DOLLARS) {
      setErr(`Minimum wallet withdrawal is $${MIN_WITHDRAW_DOLLARS}.`);
      return;
    }
    if (!isWithdraw && dollars < onlineLoadMinDollars) {
      setErr(`Minimum online checkout is $${onlineLoadMinDollars.toFixed(2)}.`);
      return;
    }
    if (!isWithdraw && onlineLoadMaxDollars && dollars > onlineLoadMaxDollars) {
      setErr(`Maximum online checkout is $${onlineLoadMaxDollars.toFixed(2)}.`);
      return;
    }
    if (!isWithdraw && allowedAmountDollars.length && !allowedAmountDollars.some((value) => Math.abs(value - dollars) < 0.001)) {
      setErr(`Choose one of the listed ${selectedPlatformLabel} amounts.`);
      return;
    }
    if (isWithdraw && (!selectedPlatform || !WITHDRAWAL_PLATFORM_TYPES.has(selectedPlatform.platform_type))) {
      setErr("Withdrawals are only available through Chime, Cash App, or Venmo cashout.");
      return;
    }
    if (isWithdraw && !note.trim()) {
      setErr(`Enter your ${payoutHint}.`);
      return;
    }
    if (isWithdraw && tipHost && (!Number.isFinite(tipDollars) || tipDollars <= 0)) {
      setErr("Choose a tip amount for your favorite host.");
      return;
    }
    if (isWithdraw && tipDollars > 0 && !tipHost) {
      setErr("Choose your favorite host for the tip.");
      return;
    }
    if (isWithdraw && tipDollars >= dollars) {
      setErr("Host tip must be less than your withdrawal amount.");
      return;
    }
    if (needsManualProof && !proofImage) {
      setErr(`Upload your ${selectedPlatformLabel || "payment"} screenshot first.`);
      return;
    }
    if (!isWithdraw && selectedPlatform && !selectedPlatform.manual && !selectedPlatform.configured) {
      setErr("Payment API is not configured for this method yet.");
      return;
    }
    setLoading(true);
    try {
      const coins = Math.round(dollars * 1000);
      const action = isWithdraw ? "Withdrawal" : "Add money";
      if (!isWithdraw && selectedPlatform?.configured) {
        const playHubCreditNote = selectedPlatform.provider === "playhubslots" && playHubWalletCreditDollars
          ? `; wallet credit $${playHubWalletCreditDollars.toFixed(2)}`
          : "";
        const { data } = await api.post(`/payments/${selectedPlatform.provider}`, {
          platform: selectedPlatform.id,
          amount: coins,
          note: `${selectedPlatformLabel || "Online"} checkout: $${dollars.toFixed(2)}${playHubCreditNote}${note ? ` - ${note}` : ""}`,
        });
        const checkoutUrl = data?.pay_url || data?.payment_url;
        if (!checkoutUrl) throw new Error("Checkout URL missing from payment provider response");
        toast.success("Opening secure checkout");
        window.location.assign(checkoutUrl);
        return;
      }
      await api.post("/me/requests", {
        type,
        amount: coins,
        game_id: null,
        payment_provider: isWithdraw ? "manual" : selectedPlatform?.manual ? "manual" : selectedPlatform?.provider || "",
        payment_platform: isWithdraw ? selectedPlatform?.platform_type || WITHDRAWAL_PLATFORM_TYPE : selectedPlatform?.manual ? selectedPlatform?.platform_type || selectedPlatform?.id || "" : selectedPlatform?.id || "",
        payment_method_id: selectedPlatform?.method_id || "",
        proof_image_url: showProofUpload ? proofImage : "",
        proof_image_name: showProofUpload ? proofName : "",
        note: `${action}${selectedPlatform?.manual ? ` via ${selectedPlatformLabel}` : ""}: $${dollars.toFixed(2)}${hasHostTip ? ` - Favorite host tip: ${tipHost} $${tipDollars.toFixed(2)} - Estimated payout after tip: $${payoutAfterTip.toFixed(2)}` : ""}${note ? ` - ${isWithdraw ? `${selectedPlatformLabel} payout: ${note}` : note}` : ""}`,
      });
      toast.success(`${title} request submitted`);
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleProof = (file) => {
    setErr("");
    setProofImage("");
    setProofName("");
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setErr("Please upload an image screenshot.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setErr("Screenshot is too large. Please upload an image under 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setProofImage(String(reader.result || ""));
      setProofName(file.name);
    };
    reader.onerror = () => setErr("Could not read screenshot. Try another image.");
    reader.readAsDataURL(file);
  };

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-black/70 p-3 py-5 pb-[calc(2rem+env(safe-area-inset-bottom))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-md rounded-2xl border border-[#D4AF37]/25 bg-[#0A130E] p-4 shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:overflow-y-auto sm:p-7"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="font-display text-xl font-bold leading-tight">{title}</h3>
            <p className="text-xs text-white/50 mt-1">
              {isWithdraw ? "Automatic payout when possible. Chime and Cash App under $200 may auto-pay; $200+ and Venmo go to manual review." : "Choose a payment method and continue checkout."}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isWithdraw && (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Cashout method</label>
              {checkout.platforms?.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
                    {checkout.platforms.map((item) => {
                      const id = `${item.provider}:${item.id}`;
                      const label = publicPaymentLabel(item.label, "Cashout");
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setPlatform(id)}
                          className={`flex min-h-[66px] items-center gap-3 rounded-xl border px-3 py-2 text-left text-sm font-bold transition ${
                            platform === id
                              ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F1C40F]"
                              : "border-white/10 bg-black/25 text-white/70 hover:bg-white/5"
                          }`}
                        >
                          <PaymentMethodIcon item={item} label={label} />
                          <span className="min-w-0">
                            <span className="block truncate leading-tight">{label}</span>
                            <span className="mt-1 block text-[10px] uppercase tracking-[0.16em] text-white/35">
                              Cashout
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="rounded-xl border border-[#D4AF37]/35 bg-[#D4AF37]/10 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-[#F8E08E]">{selectedPlatformLabel}</div>
                        <div className="mt-0.5 text-xs uppercase tracking-[0.18em] text-white/45">Request payout on this tag</div>
                      </div>
                      <span className="shrink-0 rounded-full border border-emerald-600/30 bg-emerald-900/20 px-2 py-0.5 text-xs font-bold text-emerald-300">
                        {autoCashoutEligible ? "Auto eligible" : "Manual review"}
                      </span>
                    </div>
                    {selectedPlatform?.account_value && (
                      <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">
                            {selectedPlatform.account_label || "Cashout tag"}
                          </div>
                          <div className="truncate font-mono text-sm font-bold text-white">{selectedPlatform.account_value}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyText(selectedPlatform.account_value, selectedPlatform.account_label || "Cashout tag")}
                          className="shrink-0 rounded-full border border-[#D4AF37]/35 p-2 text-[#F1C40F] hover:bg-[#D4AF37]/10"
                          aria-label="Copy cashout tag"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                    <p className="mt-3 text-xs leading-relaxed text-white/60">
                      {autoCashoutEligible
                        ? "This payout can be sent automatically through the payout network. If the payout network fails, your wallet is refunded and admin reviews it manually."
                        : "This payout will go to manual review. Admin can use the saved tag and screenshot to send it."}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-700/30 bg-amber-900/15 p-3 text-sm text-amber-200">
                  Cashout is not active yet. Ask admin to enable a Chime, Cash App, or Venmo CASHOUT method.
                </div>
              )}
            </div>
          )}

          {!isWithdraw && (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Payment method</label>
              {checkout.platforms?.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 min-[420px]:grid-cols-2">
                  {checkout.platforms.map((item) => {
                    const id = `${item.provider}:${item.id}`;
                    const label = publicPaymentLabel(item.label, "Checkout");
                    return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setPlatform(id)}
                      className={`relative flex min-h-[72px] items-center gap-3 rounded-xl border px-3 py-2 text-left text-xs font-bold transition-all min-[360px]:text-sm ${
                        platform === id
                          ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F1C40F]"
                          : "border-white/10 bg-black/25 text-white/70 hover:bg-white/5"
                      }`}
                    >
                      {item.recommended && (
                        <span className="absolute right-2 top-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.08em] text-emerald-200">
                          Recommended
                        </span>
                      )}
                      <PaymentMethodIcon item={item} label={label} />
                      <span className={`min-w-0 ${item.recommended ? "pr-16" : ""}`}>
                        <span className="block truncate leading-tight">{label}</span>
                        <span className="mt-1 block text-[10px] uppercase tracking-[0.16em] text-white/35">
                          {item.configured ? "Auto" : item.manual ? "Manual" : "Checkout"}
                        </span>
                      </span>
                    </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-white/10 bg-black/25 p-3 text-sm text-white/55">
                  Online checkout will appear here after a payment API is configured.
                </div>
              )}
              {selectedPlatform?.configured ? (
                <p className="mt-2 text-xs leading-relaxed text-white/45">You will be sent to a secure hosted checkout. Your wallet updates automatically after the provider confirms payment.</p>
              ) : selectedPlatform?.manual ? (
                <div className="mt-2 text-xs leading-relaxed text-amber-300/80">
                  <p>
                    {selectedPlatformLabel} is manual. Pay first, upload the screenshot, then admin can approve it from Telegram or the dashboard.
                  </p>
                  {selectedPlatform.account_value ? (
                    <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-white/70">
                      <span className="min-w-0 truncate">{selectedPlatform.account_label || "Send to"}: {selectedPlatform.account_value}</span>
                      <button
                        type="button"
                        onClick={() => copyText(selectedPlatform.account_value, selectedPlatform.account_label || "Payment tag")}
                        className="shrink-0 rounded-full border border-[#D4AF37]/35 p-1.5 text-[#F1C40F] hover:bg-[#D4AF37]/10"
                        aria-label="Copy payment tag"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : null}
                  {selectedPlatform.qr_code_url && (
                    <div className="mt-3 rounded-xl border border-[#D4AF37]/25 bg-black/30 p-3">
                      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F1C40F]">Scan QR code</div>
                      <img
                        src={selectedPlatform.qr_code_url}
                        alt={`${selectedPlatformLabel} QR code`}
                        className="mx-auto max-h-56 w-full rounded-lg bg-white object-contain p-2"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-xs leading-relaxed text-amber-300/80">Payment API key is not configured for this method yet. Use Chime/Venmo/PayPal manual review or ask admin to enable checkout.</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Amount ($)</label>
            <input
              type="number"
              required
              min={isWithdraw ? MIN_WITHDRAW_DOLLARS : onlineLoadMinDollars}
              max={!isWithdraw && onlineLoadMaxDollars ? onlineLoadMaxDollars : undefined}
              step={selectedPlatform?.provider === "playhubslots" ? "0.01" : allowedAmountDollars.length ? "0.01" : "1"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid={isWithdraw ? "withdraw-amount" : "add-money-amount"}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {(allowedAmountDollars.length ? allowedAmountDollars : isWithdraw ? [15, 20, 50, 100] : customAmountAllowed ? [10, 15, 20, 40, 50, 100] : [5, 10, 20, 50, 100]).map((v) => (
              <button key={v} type="button" onClick={() => setAmount(allowedAmountDollars.length ? Number(v).toFixed(2) : String(v))}
                className="px-3 py-1 text-xs rounded-full border border-white/10 hover:bg-white/5">
                ${Number(v).toFixed(allowedAmountDollars.length ? 2 : 0)}
              </button>
            ))}
          </div>
          {!isWithdraw && selectedPlatform?.provider === "playhubslots" && playHubWalletCreditDollars && Math.abs(playHubWalletCreditDollars - Number(amount || 0)) > 0.001 && (
            <p className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-200">
              Pay ${Number(amount || 0).toFixed(2)}. Wallet credits ${playHubWalletCreditDollars.toFixed(2)} after payment confirms.
            </p>
          )}

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">
              {isWithdraw ? `${selectedPlatformLabel} payout details` : "Note (optional)"}
            </label>
            <input
              type="text"
              required={isWithdraw}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isWithdraw ? payoutHint : "Payment note"}
              className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none text-sm"
            />
            {isWithdraw && (
              <p className="mt-2 text-xs leading-relaxed text-white/45">
                Enter where admin should send your withdrawal. Minimum cashout is ${MIN_WITHDRAW_DOLLARS}.
                {" "}Wallet deposits must be loaded into a game before cashout. After play, the amount redeemed back from the game is withdrawable.
                {transferLockedDollars > 0 ? ` $${transferLockedDollars.toFixed(2)} is game-load only and not withdrawable.` : ""}
              </p>
            )}
          </div>

          {isWithdraw && (
            <div className="rounded-xl border border-[#D4AF37]/20 bg-black/25 p-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F1C40F]">Want to tip your favorite host?</div>
              <div className="grid grid-cols-3 gap-2">
                {FAVORITE_HOSTS.map((host) => (
                  <button
                    key={host}
                    type="button"
                    onClick={() => setTipHost((current) => (current === host ? "" : host))}
                    className={`rounded-xl border px-2 py-2 text-xs font-bold transition ${
                      tipHost === host
                        ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F1C40F]"
                        : "border-white/10 bg-white/[0.04] text-white/70 hover:border-[#D4AF37]/50"
                    }`}
                  >
                    {host}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {HOST_TIP_AMOUNTS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setTipManual(false);
                      setTipAmount((current) => (current === String(value) ? "" : String(value)));
                    }}
                    className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
                      !tipManual && tipAmount === String(value)
                        ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F1C40F]"
                        : "border-white/10 text-white/60 hover:bg-white/5"
                    }`}
                  >
                    ${value}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setTipManual(true);
                    setTipAmount("");
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
                    tipManual
                      ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F1C40F]"
                      : "border-white/10 text-white/60 hover:bg-white/5"
                  }`}
                >
                  Manual
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTipHost("");
                    setTipAmount("");
                    setTipManual(false);
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
                    !tipHost && !tipAmount && !tipManual
                      ? "border-white/20 bg-white/[0.06] text-white/70"
                      : "border-white/10 text-white/45 hover:bg-white/5"
                  }`}
                >
                  No tip
                </button>
              </div>
              {tipManual && (
                <div className="mt-3">
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">Manual tip amount ($)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(e.target.value)}
                    placeholder="Enter custom tip"
                    className="w-full rounded-xl border border-white/10 bg-black/35 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                  />
                </div>
              )}
              {hasHostTip && (
                <p className="mt-2 text-xs leading-relaxed text-white/45">
                  Host tip ${tipDollars.toFixed(2)} for {tipHost}. Estimated payout after tip: ${payoutAfterTip.toFixed(2)}.
                </p>
              )}
            </div>
          )}

          {showProofUpload && (
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">
                {isWithdraw ? "Tag / QR screenshot (optional)" : "Payment screenshot"}
              </label>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4AF37]/35 bg-black/30 px-4 py-3 text-sm font-bold text-[#F1C40F] hover:bg-[#D4AF37]/10">
                <ImagePlus className="h-4 w-4" />
                {proofName || (isWithdraw ? "Upload tag or QR screenshot (optional)" : `Upload ${selectedPlatformLabel || "payment"} screenshot`)}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleProof(e.target.files?.[0])}
                />
              </label>
              {proofImage && (
                <img src={proofImage} alt={isWithdraw ? "Payout tag proof" : "Payment proof"} className="mt-3 max-h-40 w-full rounded-xl border border-white/10 object-contain bg-black/30" />
              )}
            </div>
          )}

          {err && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">{err}</div>}

          <button type="submit" disabled={loading}
            className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#F1C40F] transition-all disabled:opacity-50">
            {loading ? "Submitting..." : !isWithdraw && selectedPlatform?.configured ? `Continue to ${selectedPlatformLabel || "checkout"}` : `Submit ${isWithdraw ? "withdrawal" : "add money"} request`}
          </button>
        </form>
      </div>
    </div>
  );
}

function Reel({ emoji }) {
  return (
    <div className="w-16 h-20 rounded-lg bg-gradient-to-b from-[#3a1d52] to-[#1a0a2b] border-2 border-[#D4AF37] grid place-items-center font-display font-black text-3xl gold-text shadow-lg shadow-purple-900/50 shrink-0">
      {emoji}
    </div>
  );
}
